import { useState, useMemo } from 'react';
import { useAgentTemplates } from '../hooks/useAgentLibrary';
import { TemplateEditor } from './AgentLibrary';
import Modal from '../components/ui/Modal';

// ─── Icon (Lucide-Style, inline) ────────────────────────────────────────────
function Icon({ path, className = 'w-6 h-6' }) {
  return (
    <svg
      className={`${className} shrink-0`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: path }}
    />
  );
}

const IC = {
  crown:     '<path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"/><path d="M5 21h14"/>',
  search:    '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  megaphone: '<path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>',
  funnel:    '<path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3Z"/>',
  flask:     '<path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2"/><path d="M8.5 2h7"/><path d="M7 16h10"/>',
  code:      '<path d="m16 18 6-6-6-6"/><path d="m8 6-6 6 6 6"/>',
  report:    '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"/><path d="M14 2v5h5"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/>',
  plus:      '<path d="M5 12h14"/><path d="M12 5v14"/>',
  bot:       '<rect x="4" y="6" width="16" height="12" rx="2"/><path d="M9 2v4"/><path d="M15 2v4"/><path d="M2 12h2"/><path d="M20 12h2"/><circle cx="10" cy="12" r="1.3"/><circle cx="14" cy="12" r="1.3"/>',
};

// ─── Agent-Archetypen (Farbstil aus Club App) ───────────────────────────────
// Club-Palette: navy #1a2e44 · navy-l #243d5c · navy-d #111f30 ·
// gold #c9a96e · green #2d7d6f · purple #7c4dba · red #dc2626
const ARCHETYPES = [
  {
    kind: 'lead', label: 'Über-Agent', tag: 'Orchestrator', color: '#111f30', icon: IC.crown,
    desc: 'Koordiniert alle Sub-Agenten eines Kunden. Bekommt später den Kundennamen.',
  },
  {
    kind: 'seo', label: 'SEO', tag: 'Suchmaschinenoptimierung', color: '#2d7d6f', icon: IC.search,
    desc: 'Rankings, Keywords, OnPage- und technisches SEO.',
  },
  {
    kind: 'sea', label: 'SEA', tag: 'Google Anzeigen', color: '#dc2626', icon: IC.megaphone,
    desc: 'Kampagnen, Budgets und Performance in Google Ads.',
  },
  {
    kind: 'cro', label: 'CRO', tag: 'Conversion', color: '#c9a96e', icon: IC.funnel,
    desc: 'Conversion-Analyse und Optimierungsmaßnahmen.',
  },
  {
    kind: 'ab', label: 'AB', tag: 'A/B-Tests', color: '#7c4dba', icon: IC.flask,
    desc: 'Plant, startet und wertet A/B-Tests aus.',
  },
  {
    kind: 'dev', label: 'DEV', tag: 'Entwickler', color: '#243d5c', icon: IC.code,
    desc: 'Setzt technische Maßnahmen und Tracking-Code um.',
  },
  {
    kind: 'reporting', label: 'RE', tag: 'Reporting', color: '#1a2e44', icon: IC.report,
    desc: 'Versendet vorgefertigte Reports in fester Optik an den Kunden.',
  },
];

export default function AgentOverview() {
  const { data, isLoading } = useAgentTemplates();
  const [detail, setDetail] = useState(null);       // gewähltes Archetyp-Objekt
  const [editingTemplate, setEditingTemplate] = useState(null);

  const templates = data?.templates || [];

  // Kennzahlen je Archetyp (kind): Anzahl Agenten + Summe Skills
  const statsByKind = useMemo(() => {
    const m = {};
    for (const t of templates) {
      const k = t.kind || 'custom';
      if (!m[k]) m[k] = { count: 0, skills: 0 };
      m[k].count += 1;
      m[k].skills += t.skills?.length || 0;
    }
    return m;
  }, [templates]);

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50]">Agent Library</h1>
        <p className="text-[13.5px] text-gray-500 mt-1 max-w-2xl">
          Optische Übersicht aller Agenten und ihrer Fähigkeiten. Klick auf ein Icon, um Skills,
          Fähigkeiten und Modell zu bearbeiten oder neu anzulegen.
        </p>
      </div>

      {isLoading ? (
        <div className="text-[13px] text-gray-500 p-6">Lade…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {ARCHETYPES.map((a) => (
            <AgentCard
              key={a.kind}
              archetype={a}
              stats={statsByKind[a.kind]}
              onClick={() => setDetail(a)}
            />
          ))}

          {/* + weiteren Agent bauen */}
          <button
            onClick={() => setEditingTemplate({ kind: 'custom' })}
            className="group flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-white/40 p-5 min-h-[170px] text-gray-400 hover:border-[#0D3B50]/40 hover:text-[#0D3B50] transition-colors"
          >
            <span className="w-12 h-12 rounded-xl border-2 border-dashed border-current flex items-center justify-center">
              <Icon path={IC.plus} />
            </span>
            <span className="text-[13px] font-medium">Agent bauen</span>
          </button>
        </div>
      )}

      {detail && (
        <AgentDetailModal
          archetype={detail}
          templates={templates.filter((t) => (t.kind || 'custom') === detail.kind)}
          onClose={() => setDetail(null)}
          onEdit={(tpl) => setEditingTemplate(tpl)}
          onCreate={() => setEditingTemplate({ kind: detail.kind })}
        />
      )}

      {editingTemplate && (
        <TemplateEditor template={editingTemplate} onClose={() => setEditingTemplate(null)} />
      )}
    </div>
  );
}

// ─── Agent-Kachel ────────────────────────────────────────────────────────────
function AgentCard({ archetype, stats, onClick }) {
  const { label, tag, desc, color, icon } = archetype;
  const count = stats?.count || 0;
  const skills = stats?.skills || 0;

  return (
    <button
      onClick={onClick}
      className="group text-left flex flex-col rounded-2xl border border-black/[.07] bg-white p-5 min-h-[170px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm mb-3"
        style={{ backgroundColor: color }}
      >
        <Icon path={icon} />
      </div>
      <h3 className="font-semibold text-[15px] text-[#111f30] leading-tight">{label}</h3>
      <div className="text-[11px] uppercase tracking-wider font-medium mt-0.5" style={{ color }}>
        {tag}
      </div>
      <p className="text-[12px] text-gray-500 mt-2 line-clamp-2 flex-1">{desc}</p>
      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 text-[11.5px] text-gray-500">
        <span>{count} Agent{count === 1 ? '' : 'en'}</span>
        <span className="text-gray-300">·</span>
        <span>{skills} Skill{skills === 1 ? '' : 's'}</span>
      </div>
    </button>
  );
}

// ─── Detail-Modal: Agenten dieses Typs verwalten ────────────────────────────
function AgentDetailModal({ archetype, templates, onClose, onEdit, onCreate }) {
  const { label, tag, desc, color, icon } = archetype;

  return (
    <Modal onClose={onClose}>
      <div className="w-[min(560px,92vw)] max-h-[85vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white shrink-0"
            style={{ backgroundColor: color }}
          >
            <Icon path={icon} />
          </div>
          <div className="min-w-0">
            <h2 className="text-[18px] font-semibold text-[#111f30] leading-tight">{label}</h2>
            <div className="text-[11.5px] uppercase tracking-wider font-medium" style={{ color }}>
              {tag}
            </div>
            <p className="text-[12.5px] text-gray-500 mt-1">{desc}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[12.5px] font-medium text-gray-500 uppercase tracking-wider">
            Agenten ({templates.length})
          </h3>
          <button
            onClick={onCreate}
            className="text-[12.5px] px-3 py-1.5 bg-[#0D3B50] text-white rounded-lg hover:opacity-90"
          >
            + {label}-Agent anlegen
          </button>
        </div>

        {templates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center">
            <div className="text-[13px] text-gray-600 font-medium mb-1">Noch kein {label}-Agent</div>
            <div className="text-[12px] text-gray-400">
              Lege den ersten Agenten an und ordne ihm Skills, Fähigkeiten und ein Modell zu.
            </div>
          </div>
        ) : (
          <ul className="space-y-2">
            {templates.map((t) => (
              <li
                key={t.id}
                className="flex items-center gap-3 rounded-xl border border-black/[.07] bg-white p-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[13.5px] text-[#0D3B50] truncate">
                      {t.display_name}
                    </span>
                    {t.parent_template_id && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                        Sub-Agent
                      </span>
                    )}
                    {!t.is_active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                        inaktiv
                      </span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-gray-400 mt-0.5">
                    {t.skills?.length || 0} Skill{(t.skills?.length || 0) === 1 ? '' : 's'}
                    {t.model && <> · <code className="text-gray-500">{t.model}</code></>}
                  </div>
                </div>
                <button
                  onClick={() => onEdit(t)}
                  className="text-[12px] px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 shrink-0"
                >
                  Bearbeiten
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end mt-5 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Schließen
          </button>
        </div>
      </div>
    </Modal>
  );
}
